/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { ArrowForwardIos, CheckCircle, Close, Download, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight, Unpublished } from "@mui/icons-material";
import { Theme as MaterialTheme, Paper, Stack, Typography, useTheme, Button, Grid, InputLabel, FormControl, Select, SelectChangeEvent, MenuItem, useMediaQuery } from "@mui/material";
import { useState, useEffect, } from "react";

import { ISourceDocumentModel, IFileModel, IFileSortItem } from '../../../service/model/ISourceDocumentModel';
import SourceDocumentService from '../../../service/SourceDocumentService';

import { useAppBarHeight, useDebounce } from '../../../common/CustomHooks';
import DatagridVoucher from '../components/DatagridVoucher';
import DatagridVoucherDoc from '../components/DatagridVoucherDoc';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FileViewer from '../components/FileViewer';
import { useParams } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import { digiAccGuiStates, digiAccGuiTypes } from '../../../common/TextTypes';
import SourceDocumentBusinessLogic from '../../../businessLogic/SourceDocumentBusinessLogic';


const DocumentApprovalRender = ({ doc }: { doc: ISourceDocumentModel }): JSX.Element => {

    const theme = useTheme();
    function colorCode(status: string) {
        if (status == "Pending")
            return theme.palette.grey[300]
        else if (status == "Postponed")
            return theme.palette.warning.main
        else if (status == "Completed")
            return theme.palette.success.main
        else if (status == "Abstained")
            return theme.palette.grey[300]
        else //Rejected
            return theme.palette.error.main
    }
    const FinalCheckMark = (): JSX.Element => {
        if (doc.approvalRequests.find(e => e.status === "Rejected"))
            return <Unpublished fontSize="large" color="error" />
        else if (doc.approvalRequests.find(e => e.status === "Pending" || e.status === "Postponed"))
            return <Unpublished fontSize="large" htmlColor={theme.palette.grey[300]} />
        else
            return <CheckCircle fontSize="large" color="success" />
    }
    if (doc.approvalRequests && doc.approvalRequests.length > 0) {
        return <Grid container item padding={theme.spacing(2)} wrap="nowrap" sx={{ overlowX: "scroll", overflowY: "hidden" }}>
            <Stack direction="row" alignItems="center">
                {doc.approvalRequests.map(d =>
                    <>
                        <Paper
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                                bgcolor: colorCode(d.status),
                                minWidth: "8rem",
                            }}>
                            <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                                {d.userFullName}
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                                {d.finished ? new Date(d.finished).toLocaleDateString("nb-NO") + " " + new Date(d.finished).toLocaleTimeString("nb-NO").substring(0, 5) : ""}
                            </Typography>
                        </Paper>
                        <ArrowForwardIos fontSize="large" color="info" />
                    </>
                )}
                <FinalCheckMark />
            </Stack>
        </Grid>
    }
    else {
        return <></>
    }
}

const VoucherRoute = (): JSX.Element => {

    const { organizationId } = useParams();

    const theme = useTheme();

    const { t } = useTranslation();

    const [loader, setLoader] = useState(false);

    //##########################################################################################################
    const [sourceDocuments, setSourceDocuments] = useState<ISourceDocumentModel[] | null>(null);
    const [sourceDocumentsTablePageNum, setSourceDocumentsTablePageNum] = useState(0);
    function sourceDocumentsTablePageChange(page: number) {
        setSourceDocumentsTablePageNum(page);
    }
    const [sourceDocumentsTableLimitPerPage, setSourceDocumentsTableLimitPerPage] = useState(100);
    const [sourceDocumentsTableTotalPages, setSourceDocumentsTableTotalPages] = useState(0);
    const [sourceDocumentTotalItems, setSourceDocumentTotalItems] = useState(0);
    const [sourceDocumentLoader, setSourceDocumentLoader] = useState(false);
    const [sourceDocumentsIndex, setSourceDocumentsIndex] = useState<number | null>(null); 
    const [errors, setErrors] = useState<string[]>([]);
    const [sortDocumentItem, setSortDocumentItem] = useState<IFileSortItem | null>();
    function handleDocumentSort(sortItem: IFileSortItem | null) {
        setSortDocumentItem(sortItem);
    }
    //###############################################################################################################################################
    //###############################################################################################################################################
    const [voucherDateFrom, setVoucherDateFrom] = useState<Date | undefined>();
    function handleVoucherDateFrom(newDate?: Date) {
        setVoucherDateFrom(newDate);
    }
    const [voucherDateTo, setVoucherDateTo] = useState<Date | undefined>();
    function handleVoucherDateTo(newDate?: Date) {
        setVoucherDateTo(newDate);
    }
    //##
    const [dueDateFrom, setDueDateFrom] = useState<Date | undefined>();
    function handleDueDateFrom(newDate?: Date) {
        setDueDateFrom(newDate);
    }
    const [dueDateTo, setDueDateTo] = useState<Date | undefined>();
    function handleDueDateTo(newDate?: Date) {
        setDueDateTo(newDate);
    }
    //##
    const [valueDateFrom, setValueDateFrom] = useState<Date | undefined>();
    function handleValueDateFrom(newDate?: Date) {
        setValueDateFrom(newDate);
    }
    const [valueDateTo, setValueDateTo] = useState<Date | undefined>();
    function handleValueDateTo(newDate?: Date) {
        setValueDateTo(newDate);
    }
    //##
    const [createdDateFrom, setCreatedDateFrom] = useState<Date | undefined>();
    function handleCreatedDateFrom(newDate?: Date) {
        setCreatedDateFrom(newDate);
    }
    const [createdDateTo, setCreatedDateTo] = useState<Date | undefined>();
    function handleCreatedDateTo(newDate?: Date) {
        setCreatedDateTo(newDate);
    }
    //###############################################################################################################################################
    //###############################################################################################################################################
    const [voucherStatus, setVoucherStatus] = useState<digiAccGuiStates>("All");
    const handleVoucherStatusChange = (event: SelectChangeEvent) => {
        setVoucherStatus(event.target.value as digiAccGuiStates);
    };
    const [voucherType, setVoucherType] = useState<digiAccGuiTypes>("All");
    const handleVoucherTypeChange = (event: SelectChangeEvent) => {
        setVoucherType(event.target.value as digiAccGuiTypes);
    };
    //#################################################################################
    const [invoiceId, setInvoiceId] = useState<string>("");
    const debouncedInvoiceId = useDebounce(invoiceId, 700);
    //##
    const [debetId, setDebetId] = useState<string>("");
    const debouncedDebetId = useDebounce(debetId, 700);
    //##
    const [creditId, setCreditId] = useState<string>("");
    const debouncedCreditId = useDebounce(creditId, 700);
    //##
    const [departmentId, setDepartmentId] = useState<string>("");
    const debouncedDepartmentId = useDebounce(departmentId, 700);
    //##
    const [projectId, setProjectId] = useState<string>("");
    const debouncedProjectId = useDebounce(projectId, 700);
    //#################################################################################
    const [voucherIdFrom, setVoucherIdFrom] = useState<string>("");
    const debouncedVoucherIdFrom = useDebounce(voucherIdFrom, 700);
    const [voucherIdTo, setVoucherIdTo] = useState<string>("");
    const debouncedVoucherIdTo = useDebounce(voucherIdTo, 700);
    //##
    const [amountFrom, setAmountFrom] = useState<string>("");
    const debouncedAmountFrom = useDebounce(amountFrom, 700);
    const [amountTo, setAmountTo] = useState<string>("");
    const debouncedAmountTo = useDebounce(amountTo, 700);
    //#################################################################################
    const [freeTextSearch, setFreeTextSearch] = useState<string>("");
    const debouncedFreeTextSearch = useDebounce(freeTextSearch, 700);
    //#################################################################################
    
    //###############################################################################################################################################
    //###############################################################################################################################################
    //Handle document  view with grid
    const [showDoc, setShowDoc] = useState(false);
    const [expandLargeView, setExpandLargeView] = useState(false);
    function showDocToggle(index?: number) {
        if (index === 0 || index) {
            setSourceDocumentsIndex(index);
            setShowDoc(true);
        } else {
            setSourceDocumentsIndex(null)
            setShowDoc(false);
        }
    }
    function toggleExpandLargeView() {
        setExpandLargeView(!expandLargeView)
    }

    const handleFileViewedChange = (event: SelectChangeEvent) => {
        if (sourceDocuments && (sourceDocumentsIndex === 0 || sourceDocumentsIndex)) {
            let file = sourceDocuments[sourceDocumentsIndex].files?.find(e => e.id === parseInt(event.target.value));
            if(file)
                setFileViewed(file);
            else
                setFileViewed(null);
        }
    };
    const [fileViewed, setFileViewed] = useState<IFileModel | null>(null);
    //###############################################################################################################################################
    //###############################################################################################################################################
    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################
    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        const service = new SourceDocumentService();
        if (isMounted) setSourceDocumentLoader(true); //Loader for datagrid

        const getData = async () => {
            const result = await service.getSourceDocuments(
                organizationId,
                sourceDocumentsTablePageNum,
                sourceDocumentsTableLimitPerPage,
                voucherDateFrom,
                voucherDateTo,
                dueDateFrom,
                dueDateTo,
                valueDateFrom,
                valueDateTo,
                createdDateFrom,
                createdDateTo,
                [voucherStatus],
                voucherType,
                debouncedInvoiceId,
                debouncedDebetId,
                debouncedCreditId,
                debouncedDepartmentId,
                debouncedProjectId,
                debouncedVoucherIdFrom,
                debouncedVoucherIdTo,
                debouncedAmountFrom,
                debouncedAmountTo,
                debouncedFreeTextSearch,
                sortDocumentItem ? sortDocumentItem : undefined,
                
            );
            if (!result.hasErrors) {
                if (isMounted) setSourceDocuments(result.value)
                if (isMounted) setSourceDocumentsTableTotalPages(result.totalPages);
                if (isMounted) setSourceDocumentTotalItems(result.totalItems);
                if (isMounted) setErrors([])
            } else {
                setErrors(result.errors);
            }
            setSourceDocumentLoader(false);
        };
        //Close current window when searching for something new
        showDocToggle()
        getData();

        return () => { isMounted = false };
    }, [
        sourceDocumentsTablePageNum,
        sourceDocumentsTableLimitPerPage,
        voucherDateFrom,
        voucherDateTo,
        dueDateFrom,
        dueDateTo,
        valueDateFrom,
        valueDateTo,
        createdDateFrom,
        createdDateTo,
        voucherStatus,
        voucherType,
        debouncedInvoiceId,
        debouncedDebetId,
        debouncedCreditId,
        debouncedDepartmentId,
        debouncedProjectId,
        debouncedVoucherIdFrom,
        debouncedVoucherIdTo,
        debouncedAmountFrom,
        debouncedAmountTo,
        debouncedFreeTextSearch,
        sortDocumentItem,
    ]);

    const logicGetFile = async (org: string, fileId: number, sourceId: string) => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsFile(
            org,
            fileId,
            sourceId
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = document.createElement("a");
            link.download = "voucher" + sourceId + "-" + fileId;
            link.href = blobUrl;
            link.click();
        }
    }
    const logicGetFilePDF = async (org: string, fileId: number, sourceId: string) => {
        const logic = new SourceDocumentBusinessLogic();
        setLoader(true)
        const result = await logic.getSourceDocumentsFileDownloadPDF(
            org,
            fileId,
            sourceId
        );
        setLoader(false);
        if (result.hasErrors || !result.value) {
            toast(t("search.noFile") + ": " + result.errors.join(", "));
        } else {
            var blobUrl = URL.createObjectURL(result.value);

            var link = document.createElement("a");
            link.download = "voucher" + sourceId + "-" + fileId + ".pdf";
            link.href = blobUrl;
            link.click();
        }
    }

    useEffect(() => {
        if (sourceDocuments &&
            (sourceDocumentsIndex === 0 || sourceDocumentsIndex) &&
            sourceDocuments[sourceDocumentsIndex].files &&
            (sourceDocuments[sourceDocumentsIndex].files as IFileModel[]).length > 0) {
            //Making sure primary file is showed first.
            let index = (sourceDocuments[sourceDocumentsIndex].files as IFileModel[]).findIndex(f => f.isPrimary === true);
            if (index !== -1) {
                setFileViewed((sourceDocuments[sourceDocumentsIndex].files as IFileModel[])[index]);
            } else {
                setFileViewed((sourceDocuments[sourceDocumentsIndex].files as IFileModel[])[0]);
            }
        }

    }, [sourceDocumentsIndex])
    //###############################################################################################################################################
    //###############################################################################################################################################
    const isXLAndUpScreen = useMediaQuery(theme.breakpoints.up('xl')) //Check if user has XLscreen
    
    //###############################################################################################################################################
    //###############################################################################################################################################
    const appBarHeight = useAppBarHeight();
    const isLgAndUpScreen = useMediaQuery(theme.breakpoints.up('lg')) //Check if user has LgScreen and up
    const isSmAndUpScreen = useMediaQuery(theme.breakpoints.up('sm')) //Check if user has MdScreen and up

    const notANumber = () => toast(t("search.onlyNumbers"))
    //########################################################################################
    //########################################################################################
    return <main>
        <Grid container component={Paper} direction="column" wrap="nowrap"
            sx={{
                paddingTop: theme.spacing(2),
                width: "100%",
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
        }}>
            <SearchBar
                page={sourceDocumentsTablePageNum}
                limit={sourceDocumentsTableLimitPerPage}
                voucherDateFrom={voucherDateFrom}
                handleVoucherDateFrom={handleVoucherDateFrom}
                voucherDateTo={voucherDateTo} handleVoucherDateTo={handleVoucherDateTo}
                dueDateFrom={dueDateFrom}
                handleDueDateFrom={handleDueDateFrom}
                dueDateTo={dueDateTo}
                handleDueDateTo={handleDueDateTo}
                valueDateFrom={valueDateFrom}
                handleValueDateFrom={handleValueDateFrom}
                valueDateTo={valueDateTo}
                handleValueDateTo={handleValueDateTo}
                createdDateFrom={createdDateFrom}
                handleCreatedDateFrom={handleCreatedDateFrom}
                createdDateTo={createdDateTo}
                handleCreatedDateTo={handleCreatedDateTo}
                voucherType={voucherType}
                handleVoucherTypeChange={handleVoucherTypeChange}
                voucherStatus={voucherStatus}
                handleVoucherStatusChange={handleVoucherStatusChange}
                invoiceId={invoiceId}
                setInvoiceId={setInvoiceId}
                debetId={debetId}
                setDebetId={setDebetId}
                creditId={creditId}
                setCreditId={setCreditId}
                departmentId={departmentId}
                setDepartmentId={setDepartmentId}
                projectId={projectId}
                setProjectId={setProjectId}
                voucherIdFrom={voucherIdFrom}
                setVoucherIdFrom={setVoucherIdFrom}
                voucherIdTo={voucherIdTo}
                setVoucherIdTo={setVoucherIdTo}
                amountFrom={amountFrom}
                setAmountFrom={setAmountFrom}
                amountTo={amountTo}
                setAmountTo={setAmountTo}
                freeTextSearch={freeTextSearch}
                setFreeTextSearch={setFreeTextSearch}
                sortDocumentField={sortDocumentItem ? sortDocumentItem : undefined} />
            <Grid container flex="1 1 auto">
                <Grid item xs={showDoc ? 5 : 12} display={showDoc && expandLargeView ? "none" : "flex"}>
                    <DatagridVoucher
                        rows={sourceDocuments}
                        errors={errors}
                        limitPerPage={sourceDocumentsTableLimitPerPage}
                        totalItems={sourceDocumentTotalItems}
                        loader={sourceDocumentLoader}
                        changePage={sourceDocumentsTablePageChange}
                        toggleDocView={showDocToggle}
                        sortItem={handleDocumentSort}
                        activeIndex={sourceDocumentsIndex} />
                </Grid>
                {
                    sourceDocuments !== null && sourceDocumentsIndex !== null && sourceDocuments[sourceDocumentsIndex] !== null && fileViewed != null ?
                        <Grid item component={Paper} elevation={8}
                            xs={expandLargeView || !isXLAndUpScreen ? 12 : 7} direction="column" display={showDoc ? "flex" : "none"}>
                            <Grid item>
                                <Stack direction="row" margin={theme.spacing(2)} justifyContent="space-between">
                                    <Stack direction="row" spacing={theme.spacing(2)}>
                                        <FormControl color="warning" size="small" sx={{ minWidth: "11rem", maxWidth: "16rem" }}>
                                            <InputLabel id="select-doc">{t("search.documentChoice")}</InputLabel>
                                            <Select color="warning"
                                                labelId="select-doc-label"
                                                id="select-doc"
                                                value={fileViewed?.id.toString()}
                                                label={t("search.documentChoice")}
                                                onChange={handleFileViewedChange}
                                            >
                                                {sourceDocuments[sourceDocumentsIndex].files?.map(f =>
                                                    <MenuItem value={f.id}>{f.fileName}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                        {
                                            fileViewed != null && fileViewed.fileName?.substring(fileViewed.fileName.length - 3) === "xml" && sourceDocuments !== null && sourceDocumentsIndex !== null ?
                                                <>
                                                    <Button color="warning" sx={{ width: "4rem" }}
                                                        onClick={() => logicGetFile(organizationId ? organizationId : "", fileViewed.id, sourceDocuments[sourceDocumentsIndex].id + "")}
                                                        variant="outlined" size="small">
                                                        <Download fontSize="small" />
                                                    </Button>
                                                    <Button color="warning"
                                                        onClick={() => logicGetFilePDF(organizationId ? organizationId : "", fileViewed.id, sourceDocuments[sourceDocumentsIndex].id + "")}
                                                        startIcon={<Download />}
                                                        variant="outlined" size="small" sx={{ width: "11rem", }}>
                                                        {t("search.download")}
                                                    </Button>
                                                </>
                                                :
                                                fileViewed != null && sourceDocuments !== null && sourceDocumentsIndex !== null ?
                                                    <Button color="warning" sx={{ width: "4rem" }}
                                                        onClick={() => logicGetFile(organizationId ? organizationId : "", fileViewed.id, sourceDocuments[sourceDocumentsIndex].id + "")}
                                                        variant="outlined" size="small">
                                                        <Download fontSize="small" />
                                                    </Button>
                                                    :
                                                    <></>
                                        }
                                    </Stack>
                                    {
                                        isXLAndUpScreen ?
                                            <Stack direction="row" spacing={theme.spacing(1)}>
                                                <Button color="warning"
                                                    onClick={() => toggleExpandLargeView()}
                                                    startIcon={expandLargeView ? <KeyboardDoubleArrowRight /> : <KeyboardDoubleArrowLeft />}
                                                    variant="outlined" size="small" sx={{ width: "11rem", }}>
                                                    {expandLargeView ? t("search.reduceWindow") : t("search.expandWindow")}
                                                </Button>
                                                <Button color="warning"
                                                    onClick={() => showDocToggle()}
                                                    startIcon={<Close />}
                                                    variant="outlined" size="small" sx={{ width: "11rem", }}>
                                                    {t("search.closeWindow")}
                                                </Button>
                                            </Stack>
                                            :
                                            <Button color="warning"
                                                onClick={() => showDocToggle()}
                                                startIcon={<Close />}
                                                variant="outlined" size="small" sx={{ width: "11rem", }}>
                                                {t("search.closeWindow")}
                                            </Button>
                                    }
                                </Stack>
                            </Grid>
                            <Grid item height="14rem">
                                <DatagridVoucherDoc
                                    rows={sourceDocuments[sourceDocumentsIndex].lines ?
                                        sourceDocuments[sourceDocumentsIndex].lines : undefined}
                                    errors={errors} />
                            </Grid>
                            <DocumentApprovalRender doc={sourceDocuments[sourceDocumentsIndex]} />
                            <Grid item flex="1 1 auto">
                                <FileViewer showFile={showDoc} showFileToggle={showDocToggle} sourceDocumentId={sourceDocuments[sourceDocumentsIndex].id} file={fileViewed} />
                            </Grid>
                        </Grid>
                        :
                        <Grid item component={Paper} elevation={8}
                            xs={expandLargeView || !isXLAndUpScreen ? 12 : 7} direction="column" display={showDoc ? "flex" : "none"}>
                            <Typography sx={{ height: "100%", width: "100%" }}>{t("basic.documentNoFiles")}</Typography>
                        </Grid>
                }
            </Grid>
        </Grid>
    </main>

}
export default VoucherRoute;